import gsap, { Power1, Power2, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin'

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin)

export class Gallery {
  constructor() {
    this.skateBoard = null
    this.stickyWrapper = null
    this.imageRows = null
    this.copySection = null
    this.isMobile = window.matchMedia('(max-width: 768px)').matches
    this.skateTl = null
    this.scrollTriggers = []
    this.isDestroyed = false
    this.resizeTimer = null

    // ScrollTrigger設定を初期化時に1回だけ
    ScrollTrigger.config({
      autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load', // デフォルトイベントのみ
      limitCallbacks: true, // コールバック制限
    })

    this.init()
    this.setupResizeHandler()
  }

  vw(percent) {
    return (window.innerWidth * percent) / 100
  }

  init() {
    this.cleanupAnimations()

    this.skateBoard = document.querySelector('.black-rectangle-1')
    this.stickyWrapper = document.querySelector('.sticky-wrapper')
    this.imageRows = document.querySelectorAll('.image-row')
    this.copySection = document.querySelector('.p-index_gallery__copy')

    if (this.isDestroyed) return

    this.initSkateboardAnimation()
    this.initImageScrollAnimation()
    this.initCopyAnimation()

    // 初期化後に1回だけリフレッシュ
    requestAnimationFrame(() => {
      if (!this.isDestroyed) {
        ScrollTrigger.refresh()
      }
    })
  }

  setupResizeHandler() {
    const handleResize = () => {
      if (this.isDestroyed) return

      const wasMobile = this.isMobile
      this.isMobile = window.matchMedia('(max-width: 768px)').matches

      if (wasMobile !== this.isMobile) {
        // リサイズ中はrefreshを無効化
        ScrollTrigger.config({ autoRefreshEvents: 'none' })

        this.cleanupAnimations()

        requestAnimationFrame(() => {
          if (!this.isDestroyed) {
            this.init()
            // 再初期化後にrefreshを再有効化
            ScrollTrigger.config({
              autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load',
            })
          }
        })
      }
    }

    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(handleResize, 250)
    })
  }

  getSkateboardPath() {
    const platformY = this.isMobile ? -4 : -6
    return {
      climb: [
        { x: 0, y: 0 },
        { x: this.vw(6), y: this.vw(platformY * 0.3) },
        { x: this.vw(18), y: this.vw(platformY * 0.7) },
        { x: this.vw(25), y: this.vw(platformY) },
      ],
      descend: [
        { x: this.vw(44), y: this.vw(platformY) },
        { x: this.vw(70), y: this.vw(8) },
        { x: this.vw(88), y: this.vw(20) },
        { x: this.vw(110), y: this.vw(32) },
        { x: this.vw(135), y: this.vw(40) },
      ],
    }
  }

  initSkateboardAnimation() {
    if (!this.skateBoard || this.isDestroyed) return

    const paths = this.getSkateboardPath()
    const platformY = this.isMobile ? -4 : -6

    this.skateTl = gsap.timeline({
      repeat: -1,
      repeatDelay: 1.5,
    })

    this.skateTl
      .to(this.skateBoard, {
        motionPath: {
          path: paths.climb,
          autoRotate: true,
          curviness: 0,
        },
        duration: 1,
        ease: 'circ.out',
      })
      .to(this.skateBoard, {
        rotation: 0,
        duration: 0.2,
        ease: 'power2.out',
      })
      .to(this.skateBoard, {
        x: this.vw(44),
        y: this.vw(platformY),
        rotation: 0,
        duration: 1,
        ease: 'none',
      })
      .to(this.skateBoard, {
        rotation: 20,
        duration: 0.4,
        ease: 'power4.out',
      })
      .to(
        this.skateBoard,
        {
          motionPath: {
            path: paths.descend,
            autoRotate: true,
            curviness: 0,
          },
          duration: 5,
          ease: 'expo.inOut',
        },
        '-=0.2',
      )
      .set(this.skateBoard, {
        x: 0,
        y: 0,
        rotation: -20,
      })
  }

  initImageScrollAnimation() {
    if (!this.stickyWrapper || !this.imageRows.length || this.isDestroyed) return

    this.imageRows.forEach((row) => {
      const direction = row.dataset.direction
      const isRTL = direction === 'rtl'

      const tween = gsap.fromTo(
        row,
        { xPercent: isRTL ? 50 : -50 },
        {
          xPercent: isRTL ? -50 : 50,
          ease: 'none',
          scrollTrigger: {
            trigger: this.stickyWrapper,
            start: 'top center',
            end: 'center top',
            scrub: 1,
            onUpdate: (e) => {
              // row.style.setProperty('--scroll-progress', Math.abs(e.progress - 0.5) * 2)
              row.style.setProperty('--scroll-progress', Power2.easeIn(e.progress))
            },
            invalidateOnRefresh: true,
            // refreshの競合を避ける
            onRefresh: () => {
              if (this.isDestroyed) return false
            },
          },
        },
      )

      if (tween.scrollTrigger) {
        this.scrollTriggers.push(tween.scrollTrigger)
      }
    })
  }

  initCopyAnimation() {
    if (!this.copySection || this.isDestroyed) return

    const copyText = this.copySection.querySelector('.p-index_gallery__copy__text')
    const copySkateboard = this.copySection.querySelector('.p-index_gallery__copy__skateboard')

    if (!copyText || !copySkateboard) return

    const yValue = this.isMobile ? 0 : -150

    const initialState = {
      opacity: 0,
      x: 200,
      y: yValue,
      transformOrigin: 'left center',
    }

    gsap.set(copyText, { ...initialState, skewX: -40 })
    gsap.set(copySkateboard, { ...initialState, skewX: -70 })

    const animationProps = {
      opacity: 1,
      x: 0,
      y: yValue,
      skewX: 0,
      duration: 0.4,
      ease: 'power4.out',
      scrollTrigger: {
        trigger: this.copySection,
        start: 'top 50%',
        toggleActions: 'play none none reverse',
        invalidateOnRefresh: true,
        onRefresh: () => {
          if (this.isDestroyed) return false
        },
      },
    }

    const textTween = gsap.to(copyText, animationProps)
    const skateboardTween = gsap.to(copySkateboard, { ...animationProps, delay: 0.4 })

    if (textTween.scrollTrigger) {
      this.scrollTriggers.push(textTween.scrollTrigger)
    }
    if (skateboardTween.scrollTrigger) {
      this.scrollTriggers.push(skateboardTween.scrollTrigger)
    }
  }

  cleanupAnimations() {
    // タイムラインを停止
    if (this.skateTl) {
      this.skateTl.kill()
      this.skateTl = null
    }

    // ScrollTriggerを個別に削除（refreshを呼ばない）
    this.scrollTriggers.forEach((trigger) => {
      if (trigger) {
        trigger.kill(false) // false = refreshしない
      }
    })
    this.scrollTriggers = []

    // スタイルクリア
    if (this.skateBoard) {
      this.skateBoard.style.cssText = ''
    }

    if (this.imageRows) {
      this.imageRows.forEach((row) => {
        if (row) row.style.cssText = ''
      })
    }

    if (this.copySection) {
      const copyText = this.copySection.querySelector('.p-index_gallery__copy__text')
      const copySkateboard = this.copySection.querySelector('.p-index_gallery__copy__skateboard')
      if (copyText) copyText.style.cssText = ''
      if (copySkateboard) copySkateboard.style.cssText = ''
    }
  }

  destroy() {
    this.isDestroyed = true

    // リサイズタイマーをクリア
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }

    this.cleanupAnimations()

    // 全てのScrollTriggerを削除（refreshなし）
    ScrollTrigger.getAll().forEach((trigger) => {
      trigger.kill(false)
    })
  }
}
